/**
 * Admin User Activity Widget
 * Shows online/offline users, recent logins, and activity feed
 * Features real-time presence updates via WebSocket
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { 
  Users, Clock, Activity, Circle, RefreshCw,
  ChevronLeft, ChevronRight, Zap
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { ScrollArea } from '../ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import { useWS } from '../../contexts/WebSocketContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminUserActivityWidget({ minimized = false }) {
  const [activeTab, setActiveTab] = useState('online');
  const [onlineData, setOnlineData] = useState({ online: [], offline: [], online_count: 0, total_count: 0 });
  const [loginsData, setLoginsData] = useState({ logins: [] });
  const [activityData, setActivityData] = useState({ activities: [], page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [activityPage, setActivityPage] = useState(1);
  
  // Real-time presence tracking
  const [recentlyChanged, setRecentlyChanged] = useState(new Set()); // User IDs that recently changed status
  const ws = useWS();
  const previousOnlineUsersRef = useRef(new Set());

  const fetchOnlineUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin-dashboard/user-activity/online`);
      setOnlineData(response.data);
      
      // Track which users are online for comparison
      const currentOnline = new Set(response.data.online.map(u => u.id));
      previousOnlineUsersRef.current = currentOnline;
    } catch (error) {
      console.error('Error fetching online users:', error);
    }
  }, []);

  const fetchRecentLogins = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin-dashboard/user-activity/recent-logins`);
      setLoginsData(response.data);
    } catch (error) {
      console.error('Error fetching recent logins:', error);
    }
  }, []);

  const fetchActivityFeed = useCallback(async (page = 1) => {
    try {
      const response = await axios.get(`${API}/admin-dashboard/user-activity/feed?page=${page}&limit=25`);
      setActivityData(response.data);
      setActivityPage(page);
    } catch (error) {
      console.error('Error fetching activity feed:', error);
    }
  }, []);

  const refreshData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchOnlineUsers(),
      fetchRecentLogins(),
      fetchActivityFeed(1)
    ]);
    setLoading(false);
  }, [fetchOnlineUsers, fetchRecentLogins, fetchActivityFeed]);

  // Initial data fetch and polling
  useEffect(() => {
    refreshData();
    // Refresh online status every 30 seconds
    const interval = setInterval(fetchOnlineUsers, 30000);
    return () => clearInterval(interval);
  }, [refreshData, fetchOnlineUsers]);
  
  // Real-time presence updates via WebSocket
  useEffect(() => {
    if (!ws?.onlineUsers) return;
    
    const currentOnlineFromWS = ws.onlineUsers;
    const previousOnline = previousOnlineUsersRef.current;
    
    // Find users who just came online or went offline
    const changedUsers = new Set();
    
    currentOnlineFromWS.forEach(userId => {
      if (!previousOnline.has(userId)) {
        changedUsers.add(userId); // Just came online
      }
    });
    
    previousOnline.forEach(userId => {
      if (!currentOnlineFromWS.has(userId)) {
        changedUsers.add(userId); // Just went offline
      }
    });
    
    if (changedUsers.size > 0) {
      // Mark these users as recently changed (for animation)
      setRecentlyChanged(changedUsers);
      
      // Refresh the data to get updated status
      fetchOnlineUsers();
      
      // Clear the animation after 3 seconds
      setTimeout(() => {
        setRecentlyChanged(new Set());
      }, 3000);
    }
    
    previousOnlineUsersRef.current = new Set(currentOnlineFromWS);
  }, [ws?.onlineUsers, fetchOnlineUsers]);

  if (minimized) {
    return (
      <div className="p-2 flex items-center gap-2 text-sm text-muted-foreground">
        <Users className="h-4 w-4" />
        <span>{onlineData.online_count} online / {onlineData.total_count} total</span>
        {ws?.isConnected && (
          <Zap className="h-3 w-3 text-green-500 animate-pulse" title="Live updates active" />
        )}
      </div>
    );
  }

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Never';
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      'LOGIN': 'Logged in',
      'LOGOUT': 'Logged out',
      'VIEW': 'Viewed',
      'CREATE': 'Created',
      'UPDATE': 'Updated',
      'DELETE': 'Deleted',
      'PASSWORD_CHANGE': 'Changed password',
      'ADMIN_PASSWORD_RESET': 'Reset password (admin)'
    };
    return labels[action] || action;
  };
  
  // Check if a user recently changed status (for animation)
  const isRecentlyChanged = (userId) => recentlyChanged.has(userId);

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            <Circle className="h-2 w-2 fill-green-500 text-green-500 mr-1 animate-pulse" />
            {onlineData.online_count} Online
          </Badge>
          {ws?.isConnected && (
            <Badge variant="outline" className="text-xs text-green-600 border-green-600/30 bg-green-500/10">
              <Zap className="h-2.5 w-2.5 mr-1" />
              Live
            </Badge>
          )}
        </div>
        <Button variant="ghost" size="icon" onClick={refreshData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="grid w-full grid-cols-3 mb-2">
          <TabsTrigger value="online" className="text-xs">
            <Circle className="h-3 w-3 mr-1" /> Status
          </TabsTrigger>
          <TabsTrigger value="logins" className="text-xs">
            <Clock className="h-3 w-3 mr-1" /> Logins
          </TabsTrigger>
          <TabsTrigger value="activity" className="text-xs">
            <Activity className="h-3 w-3 mr-1" /> Feed
          </TabsTrigger>
        </TabsList>

        <TabsContent value="online" className="flex-1 mt-0">
          <ScrollArea className="h-[calc(100%-2rem)]">
            <div className="space-y-1 pr-2">
              {/* Online Users */}
              {onlineData.online.map(user => (
                <div 
                  key={user.id} 
                  className={`flex items-center gap-2 p-2 rounded-lg bg-green-500/10 border border-green-500/20 transition-all duration-500 ${
                    isRecentlyChanged(user.id) 
                      ? 'animate-pulse ring-2 ring-green-500 ring-offset-1 ring-offset-background' 
                      : ''
                  }`}
                  data-testid={`user-online-${user.id}`}
                >
                  <div className="relative">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.profile_picture} />
                      <AvatarFallback className="text-xs">{user.name?.charAt(0) || '?'}</AvatarFallback>
                    </Avatar>
                    <Circle className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 fill-green-500 text-green-500 ${
                      isRecentlyChanged(user.id) ? 'animate-ping' : ''
                    }`} />
                    {/* Second circle for the ping effect base */}
                    <Circle className="absolute -bottom-0.5 -right-0.5 h-3 w-3 fill-green-500 text-green-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium truncate">{user.name}</p>
                      {isRecentlyChanged(user.id) && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 bg-green-500/20 text-green-600 border-green-500/30 animate-fade-in">
                          Just joined
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{user.role?.replace('_', ' ')}</p>
                  </div>
                </div>
              ))}
              
              {/* Offline Users */}
              {onlineData.offline.map(user => (
                <div 
                  key={user.id} 
                  className={`flex items-center gap-2 p-2 rounded-lg bg-muted/50 transition-all duration-500 ${
                    isRecentlyChanged(user.id) 
                      ? 'animate-pulse ring-2 ring-amber-500 ring-offset-1 ring-offset-background' 
                      : ''
                  }`}
                  data-testid={`user-offline-${user.id}`}
                >
                  <div className="relative">
                    <Avatar className="h-8 w-8 opacity-60">
                      <AvatarImage src={user.profile_picture} />
                      <AvatarFallback className="text-xs">{user.name?.charAt(0) || '?'}</AvatarFallback>
                    </Avatar>
                    <Circle className="absolute -bottom-0.5 -right-0.5 h-3 w-3 fill-gray-400 text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium truncate text-muted-foreground">{user.name}</p>
                      {isRecentlyChanged(user.id) && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 bg-amber-500/20 text-amber-600 border-amber-500/30 animate-fade-in">
                          Just left
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {user.last_seen ? `Last seen ${formatTime(user.last_seen)}` : 'Offline'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="logins" className="flex-1 mt-0">
          <ScrollArea className="h-[calc(100%-2rem)]">
            <div className="space-y-1 pr-2">
              {loginsData.logins.map((user, idx) => (
                <div key={`${user.id}-${idx}`} className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user.profile_picture} />
                    <AvatarFallback className="text-xs">{user.name?.charAt(0) || '?'}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatTime(user.last_login)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="activity" className="flex-1 mt-0 flex flex-col">
          <ScrollArea className="flex-1">
            <div className="space-y-1 pr-2">
              {activityData.activities.map((activity, idx) => (
                <div key={activity.id || idx} className="flex items-start gap-2 p-2 rounded-lg bg-muted/30">
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={activity.profile_picture} />
                    <AvatarFallback className="text-xs">{activity.user_name?.charAt(0) || '?'}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-medium">{activity.user_name}</span>
                      {' '}
                      <span className="text-muted-foreground">{getActionLabel(activity.action)}</span>
                      {activity.resource_type && (
                        <span className="text-muted-foreground"> {activity.resource_type}</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatTime(activity.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
          
          {/* Pagination */}
          {activityData.total_pages > 1 && (
            <div className="flex items-center justify-between pt-2 border-t mt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchActivityFeed(activityPage - 1)}
                disabled={activityPage <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground">
                Page {activityPage} of {activityData.total_pages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchActivityFeed(activityPage + 1)}
                disabled={activityPage >= activityData.total_pages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default AdminUserActivityWidget;
